import dayjs from 'dayjs';
import _ from 'lodash';
import { message } from 'antd';
import config from '@config';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { regionOptions } from '@utils/region';

export {
    formatArea,
    getCityNameByCode,
    getDistrictNameByCode,
    getLocationNameByCode,
    getProvinceNameByCode,
} from '@utils/region';

export const success = msg => message.success(msg); // 成功提示
export const error = msg => message.error(msg); // 错误提示
export const warning = msg => message.warning(msg); // 警告提示
export const info = msg => message.info(msg); // 一般提示

// 安全 JSON 解析
export const safeParseJSON = (jsonString, defaultValue = null) => {
    try {
        return JSON.parse(jsonString);
    } catch (error) {
        console.error('解析 JSON 失败:', error.message);
        return defaultValue;
    }
};

export const dateTimeFormat = (datetime, formatString = 'YYYY-MM-DD') => {
    return dayjs(datetime).format(formatString);
};

export const dateTimeFormatHMS = (datetime, formatString = 'YYYY-MM-DD HH:mm:ss') => {
    return dayjs(datetime).format(formatString);
};

/**
 * 秒 转为 时分秒
 * @param seconds
 * @returns {string}
 */
export const secondsToHHMMSS = seconds => new Date(seconds * 1000).toISOString().substr(11, 8);

export const secondsToText = seconds => {
    if (!seconds) {
        return '';
    }

    let text = '';
    const time = new Date(seconds * 1000).toISOString().substr(11, 8).split(':');
    const hour = Number(time[0]);
    const minute = Number(time[1]);
    const second = Number(time[2]);

    if (hour) {
        text = hour + '小时';
    }
    if (minute) {
        text = text + minute + '分';
    }
    if (second) {
        text = text + second + '秒';
    }

    return text;
};

export const convertTimeRangeToText = (startTime, endTime) => {
    let result = '';

    if (!!startTime && !!endTime && dayjs(startTime).isValid() && dayjs(endTime).isValid()) {
        result = dayjs(startTime).format('HH:mm') + '～' + dayjs(endTime).format('HH:mm');
    }

    return result;
};

export const convertDateTimeToLocalText = (dateTime, enableSecond = 1) => {
    let result = '';

    if (!!dateTime && dayjs(dateTime).isValid()) {
        result = dayjs(dateTime).format('YYYY年M月D日 HH:mm:ss');

        if (!enableSecond) {
            result = result.slice(0, -3);
        }
    }

    return result;
};

/**
 * 人性化展示日期范围
 * @param startDate
 * @param endDate
 * @returns {string}
 */
export const convertDateRangeToLocalText = (startDate, endDate) => {
    let result = '';

    if (!!startDate && !!endDate && dayjs(startDate).isValid() && dayjs(endDate).isValid()) {
        const startDateMoment = dayjs(startDate);
        const endDateMoment = dayjs(endDate);
        const formattedStartDate = startDateMoment.format('YYYY年M月D日');
        if (startDateMoment.format('YYYY') === endDateMoment.format('YYYY')) {
            // 同年
            if (startDateMoment.format('YYYYMM') === endDateMoment.format('YYYYMM')) {
                // 同月
                result = `${formattedStartDate}～${endDateMoment.format('D日')}`;
            } else {
                // 不同月
                result = `${formattedStartDate}～${endDateMoment.format('M月D日')}`;
            }
        } else {
            // 不同年
            result = `${formattedStartDate}～${endDateMoment.format('YYYY年M月D日')}`;
        }
    }

    return result;
};

/**
 * 人性化展示日期时间范围
 * @param startDateTime
 * @param endDateTime
 * @param enableSecond
 * @returns {string}
 */
export const convertDateTimeRangeToLocalText = (startDateTime, endDateTime, enableSecond = 1) => {
    let result = '';

    if (!!startDateTime && !!endDateTime && dayjs(startDateTime).isValid() && dayjs(endDateTime).isValid()) {
        const startDateTimeMoment = dayjs(startDateTime);
        const endDateTimeMoment = dayjs(endDateTime);

        let formattedStartDateTime = startDateTimeMoment.format('YYYY年M月D日 HH:mm:ss');
        if (!enableSecond) {
            formattedStartDateTime = formattedStartDateTime.slice(0, -3);
        }

        if (startDateTimeMoment.format('YYYY') === endDateTimeMoment.format('YYYY')) {
            // 同年
            if (startDateTimeMoment.format('YYYYMM') === endDateTimeMoment.format('YYYYMM')) {
                // 同日
                result = `${formattedStartDateTime}～${endDateTimeMoment.format('HH:mm:ss')}`;
            } else {
                // 不同日
            }
        } else {
            // 不同年
            result = `${formattedStartDateTime}～${endDateTimeMoment.format('YYYY年M月D日 HH:mm:ss')}`;
        }
    }

    if (!enableSecond) {
        result = result.slice(0, -3);
    }

    return result;
};

/**
 * 是否为空对象、数组等
 * @param {any} value - 值
 * @returns {boolean} - 是否为空对象、数组等
 */
export const isEmpty = value => _.isEmpty(value); // 是否为空对象、数组等

/**
 * 是否为数组
 * @param {any} value - 值
 * @returns {boolean} - 是否为数组
 */
export const isArray = value => _.isArray(value); // 是否为数组

/**
 * 是否为对象
 * @param {any} value - 值
 * @returns {boolean} - 是否为对象
 */
export const isObject = value => _.isObject(value); // 是否为对象

// 获取后台管理员信息
export const getAdminInfo = () => {
    const adminInfo = localStorage.getItem('infos');
    let adminId = 0;
    let adminName = '';
    let adminPhone = '';
    if (adminInfo && JSON.parse(adminInfo)) {
        const info = JSON.parse(adminInfo)?.data;
        adminId = JSON.parse(info)?.id;
        adminName = JSON.parse(info)?.name;
        adminPhone = JSON.parse(info)?.phone;
    }
    return { adminId, adminName, adminPhone };
};

/**
 * 解析专家 updateTraces（接口字段为 updateTraces，可能为对象或 JSON 字符串）
 * 单项可含：updated_at、updated_by_admin_id、updated_by_admin_name、updated_by_admin_phone；旧数据可能仅有 updated_by
 * @param {object|string|null|undefined} raw
 * @returns {Record<string, Record<string, unknown>>}
 */
export const parseExpertUpdateTraces = raw => {
    if (raw == null || raw === '') {
        return {};
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) {
        return { ...raw };
    }
    if (typeof raw === 'string') {
        const parsed = safeParseJSON(raw, {});
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    }
    return {};
};

/**
 * 合并手机号变更后的 updateTraces，供 PUT /user/ssomemberexperts/update-phone 的 updateTraces 字段（JSON 字符串）
 * @param {object|string|null|undefined} existingRaw - 当前专家已有的 updateTraces
 * @returns {string}
 */
export const buildExpertUpdateTracesJsonForPhoneChange = existingRaw => {
    const traces = parseExpertUpdateTraces(existingRaw);
    const { adminId, adminName, adminPhone } = getAdminInfo();
    const idNum = adminId != null && adminId !== '' ? Number(adminId) : NaN;
    traces.phone = {
        updated_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        updated_by_admin_id: !Number.isNaN(idNum) ? idNum : 0,
        updated_by_admin_name: adminName != null ? String(adminName) : '',
        updated_by_admin_phone: adminPhone != null ? String(adminPhone) : '',
    };
    return JSON.stringify(traces);
};

const TRACE_DIMENSION_KEYS = ['intro', 'unit', 'laborfee'];

/**
 * 编辑专家时，在个人介绍 / 单位任职 / 收款信息 有变更时合并对应维度的 update_traces，供 PUT edit 的 updateTraces 字段（JSON 字符串）
 * @param {object|string|null|undefined} existingRaw - 当前专家已有的 updateTraces
 * @param {string[]} keysToUpdate - 含 'intro' | 'unit' | 'laborfee' 中一项或多项
 * @returns {string|null} 无变更维度时返回 null
 */
export const mergeExpertUpdateTraceEntriesForEdit = (existingRaw, keysToUpdate) => {
    if (!Array.isArray(keysToUpdate) || keysToUpdate.length === 0) {
        return null;
    }
    const valid = keysToUpdate.filter(k => TRACE_DIMENSION_KEYS.includes(k));
    if (valid.length === 0) {
        return null;
    }
    const traces = parseExpertUpdateTraces(existingRaw);
    const { adminId, adminName, adminPhone } = getAdminInfo();
    const idNum = adminId != null && adminId !== '' ? Number(adminId) : NaN;
    const entry = {
        updated_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        updated_by_admin_id: !Number.isNaN(idNum) ? idNum : 0,
        updated_by_admin_name: adminName != null ? String(adminName) : '',
        updated_by_admin_phone: adminPhone != null ? String(adminPhone) : '',
    };
    valid.forEach(k => {
        traces[k] = { ...entry };
    });
    return JSON.stringify(traces);
};

/** 个人介绍 HTML 规范化后用于变更对比 */
export const normalizeExpertIntroHtmlForCompare = html => {
    if (html == null || html === '') {
        return '';
    }
    const s = String(html).trim();
    const compact = s.replace(/\s/g, '');
    if (!compact || compact === '<p><br></p>' || compact === '<p></p>' || compact === '<br>') {
        return '';
    }
    return s;
};

/**
 * 按维度 key 从专家 update_traces / updateTraces 中取单项展示数据（需存在 updated_at）
 * 常见 key：phone、unit、intro、laborfee
 *
 * @param {object} expert
 * @param {string} key - 维度键名，如 'phone' | 'unit' | 'intro' | 'laborfee'
 * @returns {{
 *   updatedAt: string,
 *   updatedByAdminId: number | null,
 *   updatedByAdminName: string,
 *   updatedByAdminPhone: string,
 *   updatedByLabel: string,
 * } | null}
 */
export const getExpertUpdateTrace = (expert, key) => {
    if (key == null || key === '') {
        return null;
    }
    const raw = expert?.updateTraces ?? expert?.update_traces;
    const traces = parseExpertUpdateTraces(raw);
    const entry = traces[key];
    if (!entry || !entry.updated_at) {
        return null;
    }

    const name = entry.updated_by_admin_name != null && String(entry.updated_by_admin_name).trim() !== '' ? String(entry.updated_by_admin_name).trim() : '';
    const phone = entry.updated_by_admin_phone != null && String(entry.updated_by_admin_phone).trim() !== '' ? String(entry.updated_by_admin_phone).trim() : '';
    const legacyBy = entry.updated_by != null && String(entry.updated_by).trim() !== '' ? String(entry.updated_by).trim() : '';

    const idRaw = entry.updated_by_admin_id;
    let updatedByAdminId = null;
    if (idRaw != null && idRaw !== '') {
        const n = Number(idRaw);
        if (!Number.isNaN(n)) {
            updatedByAdminId = n;
        }
    }

    const updatedByLabel = name || legacyBy || phone;

    return {
        updatedAt: entry.updated_at,
        updatedByAdminId,
        updatedByAdminName: name,
        updatedByAdminPhone: phone,
        updatedByLabel,
    };
};

/**
 * 获取最近num天前的日期
 * @param {number} num - 最近num天前的日期
 * @returns {string} - 最近num天前的日期
 */
export const lastDays = num => {
    const date = new Date();
    const lw = new Date(date - 1000 * 60 * 60 * 24 * num); // 最近30天（num=29）
    const lastY = lw.getFullYear();
    const lastM = lw.getMonth() + 1;
    const lastD = lw.getDate();
    const startdate = lastY + '-' + (lastM < 10 ? '0' + lastM : lastM) + '-' + (lastD < 10 ? '0' + lastD : lastD);
    return startdate;
};

/**
 * 将日期转换为字符串
 * @param {Date} date - 日期
 * @returns {string} - 日期字符串
 */
export const timeString = date => {
    const yearDate = date.getFullYear();
    const monthDate = (date.getMonth() + 1).toString().padStart(2, '0');
    const dayDate = date.getDate().toString().padStart(2, '0');
    const hourDate = date.getHours();
    const minDate = date.getMinutes();
    const secDate = date.getSeconds();
    const dateString = `${yearDate}年${monthDate}月${dayDate}日${hourDate}时${minDate}分${secDate}秒`;
    return dateString;
};

/**
 * 将日期转换为字符串
 * @param {Date} date - 日期
 * @returns {string} - 日期字符串
 */
export const todayDateString = date => {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
};

// 获取背景颜色
export const getBannerColor = url => {
    return new Promise((resolve, reject) => {
        if (!url) {
            reject('url不能为空');
        }
        const image = new Image();
        image.setAttribute('crossOrigin', 'anonymous');
        image.crossOrigin = 'Anonymous';
        image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = image.width;
            canvas.height = image.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(image, 0, 0);
            const imgData = ctx.getImageData(0, 0, 1, 1);
            const data = imgData.data;
            resolve(`rgb(${data[0]},${data[1]},${data[2]})`);
        };
        image.onerror = () => {
            reject('图片加载失败');
        };
        image.src = `${url}?tamp=${new Date().valueOf()}`;
    });
};

// 校验文件大小
//最大
export const getMaxFileWidthAndHeight = (file, initWidth, initHeight) => {
    return new Promise((resolve, reject) => {
        const width = initWidth;
        const height = initHeight;
        const _URL = window.URL || window.webkitURL;
        const img = new Image();
        img.src = _URL.createObjectURL(file);
        img.onload = () => {
            console.log(1);
            if (img.width < width || img.height < height) {
                message.error(`上传尺寸不小于${initWidth}px * ${initHeight}px!`);
                reject();
            } else {
                resolve();
            }
        };
    });
};
//最小
export const getMinFileWidthAndHeight = (file, initWidth, initHeight) => {
    return new Promise((resolve, reject) => {
        const width = initWidth;
        const height = initHeight;
        const _URL = window.URL || window.webkitURL;
        const img = new Image();
        img.src = _URL.createObjectURL(file);
        img.onload = () => {
            console.log(1);
            if (img.width < width || img.height < height) {
                message.error(`上传尺寸最小${initWidth}px * ${initHeight}px!`);
                reject();
            } else {
                resolve();
            }
        };
    });
};

// 递归遍历数组 修改元素
export const TreeDataSource = arr => {
    if (!Array.isArray(arr)) {
        return;
    }
    return arr.map(item => {
        return {
            ...item,
            title: item.name,
            value: item.id,
            key: item.id,
            children: item.childList && TreeDataSource(item.childList),
        };
    });
};

/**
 * 抹平部门树，保留所有层级节点（children 结构）
 * 用于 /enterprise/boss/departments/list 返回的多层 children 结构，
 * 列表按 id 反查名称时需覆盖任意层级被选的部门
 * @param {Array} treeData 部门树 [{ id, name, children: [...] }]
 * @returns {Array<{ id: number, name: string }>} 全量部门扁平数组
 */
export const flattenAllDepartments = treeData => {
    if (!Array.isArray(treeData)) return [];
    const result = [];
    const walk = list => {
        list.forEach(item => {
            if (!item) return;
            result.push({ id: item.id, name: item.name });
            if (Array.isArray(item.children) && item.children.length > 0) {
                walk(item.children);
            }
        });
    };
    walk(treeData);
    return result;
};

/**
 * 部门树（children 结构）转 TreeSelect treeData
 * @param {Array} treeData 部门树 [{ id, name, children: [...] }]
 * @returns {Array} TreeSelect treeData [{ title, value, key, children }]
 */
export const departmentTreeToSelectData = treeData => {
    if (!Array.isArray(treeData)) return [];
    return treeData.map(item => ({
        title: item.name,
        value: item.id,
        key: item.id,
        children: departmentTreeToSelectData(item.children),
    }));
};

// 递归treedata找到指定对象
export const getTreeObj = (data, key) => {
    for (const i in data) {
        if (data[i].moduleId === key) {
            return data[i];
        }
        if (data[i].children) {
            const node = getTreeObj(data[i].children, key);
            if (node !== undefined) {
                return node;
            }
        }
    }
};

// 排序树找到该节点的所有父节点
export const getParentIdList = (list, id) => {
    if (!list || !id) {
        return '';
    }
    const arr = [];
    const findParent = (data, nodeId, parentId) => {
        for (var i = 0, length = data.length; i < length; i++) {
            const node = data[i];
            //由于后端返回的树状结构数组⾥⾯的value值为string类型,所以为了避免判断全等有问题,所以将传⼊的城市id使⽤toString进⾏了类型转换
            if (node.key === nodeId) {
                arr.unshift(nodeId);
                if (nodeId === list[0].key) {
                    break;
                }
                findParent(list, parentId);
                break;
            } else {
                if (node.children) {
                    findParent(node.children, nodeId, node.key);
                }
            }
        }
        return arr;
    };
    return findParent(list, id);
};

/**
 * 将富文本中的图片和标签替换为[图片]和空字符串
 * @param {string} data - 富文本内容
 * @returns {string} - 替换后的富文本内容
 */
export const regRichText = data => {
    const imgreg = /<img.*?\/?>/gi;
    const reg = /<\/?.+?\/?>/g;
    data = data.replace(imgreg, '[图片]').replace(reg, '');
    return data;
};

/**
 * 将富文本中的空格、换行符、标签替换为空字符串
 * @param {string} data - 富文本内容
 * @returns {string} - 替换后的富文本内容
 */
export const emptyRichText = data => {
    let content;
    if (!data) return data;
    content = data
        .replace(/[ 　\t\r\n]+/g, '')
        .replace(/<br *\/?>/g, '')
        .replace(/<\/?p>/g, '');
    if (content === '') return content;
    else return data;
};

/**
 * 将地区信息转换为字符串
 * @returns {string} - 地区信息字符串
 */
export const areasString = (() => {
    const province = [],
        city = [],
        district = [];

    regionOptions.forEach(item => {
        province.push({ value: item.value, label: item.label });
        if (item.children) {
            item.children.forEach(it => {
                city.push({ value: it.value, label: it.label });
                if (it.children) {
                    it.children.forEach(i => {
                        district.push({ value: i.value, label: i.label });
                    });
                }
            });
        }
    });

    return {
        province,
        city,
        district,
    };
})();

// 文件上传前缀
export const getFileUrl = url => {
    if (url && url.startsWith('http')) {
        return url;
    }
    if (url && !url.startsWith('http')) {
        return config.OSS_PREFIX + url;
    }
    return null;
};

// 健康界资源前缀
export const getJklFileUrl = url => {
    if (url && url.startsWith('http')) {
        return url;
    }
    if (url && !url.startsWith('http')) {
        return config.FILE_PREFIX + url;
    }
    return null;
};

// 基于 BC_OSS_URL_PREFIX 拼接完整 URL
export const prepareBCOSSUrl = path => {
    let url = '';

    if ((typeof path === 'string' || path instanceof String) && path !== '') {
        if (path.startsWith('http')) {
            url = path;
        } else if (path.startsWith('/')) {
            url = `${config.BC_OSS_URL_PREFIX}${path}`;
        } else {
            url = `${config.BC_OSS_URL_PREFIX}/${path}`;
        }

        // 处理存量数据中 OSS_BUCKET 域名（非 DNS 加速域名）
        url = url.replace(config.BC_OSS_BUCKET, config.BC_OSS_URL_PREFIX);
    }

    return url;
};

// 获取文件blob格式
const getFileBlob = url => {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('GET', url, true);
        request.responseType = 'blob';
        request.onload = res => {
            if (res.target.status === 200) {
                resolve(res.target.response);
            } else {
                reject(res);
            }
        };
        request.send();
    });
};

// 多个文件下载(合并文件夹)
export const downLoadMultiFile = (files, filesName) => {
    message.warning('下载中');

    const zip = new JSZip();
    const result = [];
    for (const i in files) {
        const promise = getFileBlob(files[i].url).then(res => {
            zip.file(files[i].name, res, { binary: true });
        });
        result.push(promise);
    }
    Promise.all(result).then(() => {
        zip.generateAsync({ type: 'blob' }).then(res => {
            saveAs(res, filesName);
            message.success('下载完成，请在浏览器下载内容处查看');
        });
    });
};

// 截取地址栏参数
export function GetQueryString(name) {
    var reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)');
    var r = window.location.search.substr(1).match(reg);
    if (r != null) return unescape(r[2]);
    return null;
}

// 项目编号
// 医项目 YXM000001，即 YXM 后加“前位补0的6位数字”
// 医评议 YPX000001，同上
// 医会议 YHY000001，同上
export const numberProject = (projectType, no) => projectType.toUpperCase() + ('000000' + no).slice(-6);

// 格式化金额
export const formatCurrency = value => {
    let result = '';

    const f = parseFloat(value) || 0.0;
    const parts = f.toFixed(2).split('.');
    result = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') + (parts[1] ? '.' + parts[1] : '');

    return result;
};

// 滚动到表单中的第1处错误位置
export const scrollToFirstFormItemHasError = () => {
    const errorElements = document.getElementsByClassName('ant-form-item-has-error');
    if (errorElements.length) {
        const ele = errorElements[0];
        const eleTop = ele.getBoundingClientRect().top;
        const headerOffset = 48;
        const offsetPosition = eleTop + window.scrollY - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
};

/**
 * 格式化成立时间显示
 * @param {string} establishedAt - 成立时间
 * @param {string} establishedDateType - 成立时间类型 ('d', 'm', 'y')
 * @returns {string} 格式化后的时间字符串
 */
export const formatEstablishedTime = (establishedAt, establishedDateType) => {
    if (!establishedAt) {
        return '';
    }

    const date = dayjs(establishedAt);

    switch (establishedDateType) {
        case 'year':
            return date.format('YYYY年');
        case 'month':
            return date.format('YYYY年M月');
        case 'date':
        default:
            return date.format('YYYY年M月D日');
    }
};

// 审核状态标签映射
export const ReviewStatusMap = {
    0: {
        text: '待审核',
        type: 'warning',
        color: '#faad14',
    },
    1: {
        text: '已通过',
        type: 'success',
        color: '#52c41a',
    },
    2: {
        text: '已驳回',
        type: 'danger',
        color: '#ff4d4f',
    },
};

/**
 * 准备医疗机构级别和等级信息
 * - 级别为三级、二级、一级时，显示级别；等次为甲等、乙等、丙等时，显示等次
 * - 级别和等次均显示时，简化显示：三级甲等 -> 三甲，二级甲等 -> 二甲，一级甲等 -> 一甲
 * - 级别和等次有其一时，不简化
 * @param {Object} unit - 机构对象
 * @returns {string} 格式化后的等级信息
 */
export const prepareMedLevelGrade = unit => {
    let levelWithGrade = '';
    const level = unit?.medLevel;
    const grade = unit?.medGrade;

    // 判断是否有有效的级别和等次
    const hasValidLevel = level && (level === '三级' || level === '二级' || level === '一级');
    const hasValidGrade = grade && (grade === '甲等' || grade === '乙等' || grade === '丙等');

    if (hasValidLevel && hasValidGrade) {
        // 级别和等次均有时，简化显示：三级甲等 -> 三甲
        if (grade === '甲等') {
            levelWithGrade = level.replace('级', '') + '甲';
        } else if (grade === '乙等') {
            levelWithGrade = level.replace('级', '') + '乙';
        } else if (grade === '丙等') {
            levelWithGrade = level.replace('级', '') + '丙';
        }
    } else if (hasValidLevel) {
        // 只有级别时，不简化，直接显示级别
        levelWithGrade = level;
    } else if (hasValidGrade) {
        // 只有等次时，也不简化，直接显示等次
        levelWithGrade = grade;
    }

    return levelWithGrade;
};

/**
 * 准备单位所在地信息
 * @param {Object} unit - 机构对象
 * @returns {string} 格式化后的地理位置信息
 */
export const prepareLocation = unit => {
    let location = '';

    if (unit?.province) {
        location += unit.province;

        // 如果有市，且不是省直辖县级行政区划（北京、上海、天津、重庆），则添加市
        // 避免出现"北京市"为"北京"的情况
        const municipalityRegx = /^(北京|上海|天津|重庆)/;
        if (!!unit?.city && !municipalityRegx.test(unit.province)) {
            location += unit.city;
        }

        // 如果有区县，则添加区县（包括直辖市的区县）
        if (!!unit?.district) {
            location += unit.district;
        }
    }
    return location;
};

/**
 * 准备机构别称信息
 * @param {Object} unit - 机构对象
 * @returns {string} 格式化后的别称信息
 */
export const prepareOrgAliases = unit => {
    if (!unit?.aliases) return '';

    try {
        // 解析JSON格式的别称数据
        const aliases = JSON.parse(unit.aliases);
        if (!Array.isArray(aliases) || aliases.length === 0) return '';

        // 分离当前别称和曾用名
        const currentAliases = aliases.filter(alias => !alias.former);
        const formerAliases = aliases.filter(alias => alias.former);

        let aliasText = '';

        // 添加当前别称
        if (currentAliases.length > 0) {
            const currentNames = currentAliases.map(alias => alias.name).join('、');
            aliasText += currentNames;
        }

        // 添加曾用名
        if (formerAliases.length > 0) {
            const formerNames = formerAliases.map(alias => `${alias.name}(曾用名)`).join('、');
            if (!!aliasText) aliasText += '、';
            aliasText += formerNames;
        }

        return aliasText;
    } catch (error) {
        console.error('解析机构别称失败:', error);
        return '';
    }
};

/**
 * 格式化组织机构选项数据
 * @param {Array} organizations - 组织机构列表
 * @returns {Array} 格式化后的选项数据
 */
export const formatOrganizationOptions = organizations => {
    if (!Array.isArray(organizations)) return [];

    return organizations.map(org => ({
        value: org.id,
        label: org.name, // 新API使用name字段
        orgData: org, // 保存原始数据，用于显示详细信息
    }));
};

// 院士相关常量定义
export const ACADEMICIAN_TYPES = {
    CAS: '中国科学院院士', // Chinese Academy of Sciences Academician
    CAE: '中国工程院院士', // Chinese Academy of Engineering Academician
};

// 解析荣誉头衔，返回院士头衔数组（按 CAS -> CAE 顺序）
export const getAcademicianTitles = honoraryTitles => {
    if (!honoraryTitles) return [];

    let titles = [];
    try {
        if (Array.isArray(honoraryTitles)) {
            titles = honoraryTitles;
        } else if (typeof honoraryTitles === 'string') {
            try {
                const parsed = JSON.parse(honoraryTitles);
                if (Array.isArray(parsed)) {
                    titles = parsed;
                } else if (typeof parsed === 'string') {
                    titles = [parsed];
                } else if (parsed && typeof parsed === 'object') {
                    titles = Object.values(parsed);
                }
            } catch (jsonErr) {
                titles = honoraryTitles.split(/[、,，\s]+/).filter(Boolean);
            }
        }
    } catch (error) {
        console.error('荣誉头衔解析失败:', error);
    }

    const result = [];
    if (titles.some(t => t && t.includes(ACADEMICIAN_TYPES.CAS))) {
        result.push(ACADEMICIAN_TYPES.CAS);
    }
    if (titles.some(t => t && t.includes(ACADEMICIAN_TYPES.CAE))) {
        result.push(ACADEMICIAN_TYPES.CAE);
    }
    return result;
};

// 从认证字典构建映射：职务、科室 code -> name
export const buildAuthDictMaps = authDictOptions => {
    const officeCodeToName = {};
    const postCodeToName = {};

    if (Array.isArray(authDictOptions)) {
        authDictOptions.forEach(authType => {
            if (Array.isArray(authType?.labels)) {
                authType.labels.forEach(label => {
                    // 职务（扁平一层）
                    if (label?.name === '职务' && Array.isArray(label.options)) {
                        label.options.forEach(opt => {
                            if (opt?.code) {
                                postCodeToName[opt.code] = opt.name || '';
                            }
                        });
                    }
                    // 科室（分类 -> 子项）
                    if (label?.name === '科室' && Array.isArray(label.options)) {
                        label.options.forEach(category => {
                            if (Array.isArray(category?.options)) {
                                category.options.forEach(opt => {
                                    if (opt?.code) {
                                        officeCodeToName[opt.code] = opt.name || '';
                                    }
                                });
                            }
                        });
                    }
                });
            }
        });
    }

    return { officeCodeToName, postCodeToName };
};

// 根据编码获取名称（从映射中）
export const getNameByCodeFromMap = (code, map) => {
    if (!code || !map) return '';
    return map[code] || '';
};

// 格式化单位内的科室与职务显示（不插入空格）
export const formatOfficeAndPost = (workExperience, maps) => {
    if (!workExperience) return '';
    const { unitOffice, unitPost, memOfficeCode, memPostCode } = workExperience;

    let officeDisplay = unitOffice || '';
    let postDisplay = unitPost || '';

    if (!officeDisplay && memOfficeCode) {
        officeDisplay = getNameByCodeFromMap(memOfficeCode, maps?.officeCodeToName);
    }
    if (!postDisplay && memPostCode) {
        postDisplay = getNameByCodeFromMap(memPostCode, maps?.postCodeToName);
    }

    const parts = [];
    if (officeDisplay) parts.push(officeDisplay);
    if (postDisplay) parts.push(postDisplay);
    return parts.join('');
};
